import './CustomMetricsModal.scss';
import cn from 'classnames';
import { titleize } from 'inflected';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MdClose, MdSave } from 'react-icons/md';
import { Form, Modal } from 'react-bootstrap';
import {
  CROSS_CHANNEL,
  SEARCHSHOPPING,
  YOUTUBE
} from '@blisspointmedia/bpm-types/dist/MetricsTable';
import {
  CustomMetric,
  CustomMetricKPI
} from '@blisspointmedia/bpm-types/dist/pgTables/CustomMetric';
import {
  DuplicateMetricType,
  findDuplicateMetric
} from '@blisspointmedia/bpm-types/dist/customMetricValidation';
import { MAX_DECIMALS } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { KpiInfo } from '@blisspointmedia/bpm-types/dist/Kpis';
import { Button } from '@Components/Button/Button';
import { Appearance, Design, Placement, Size } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { IconButton } from '@Components/IconButton/IconButton';
import {
  ColumnMetaData,
  ColumnMetaDataMap,
  FUNCTIONS,
  GLOBAL_KPI,
  OPERATORS,
  convertKPIMapToOptions
} from '@Components/MetricsTable/metrics-table-utils';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import { SingleChannelMediaTypes } from '@src/utils/performance-utils';
import { FORMAT_OPTIONS, OPERATOR_LABEL_MAP } from './custom-metrics-modal-constants';
import {
  CLOSE_PAREN,
  OPEN_PAREN,
  suggestNextTokens,
  testFormula,
  tokenizeFormula
} from './custom-metrics-modal-utils';

const CHANNEL_DISPLAY_NAME: Record<string, string> = {
  [CROSS_CHANNEL]: 'Cross-Channel',
  [SEARCHSHOPPING]: 'Search & Shopping',
  [YOUTUBE]: 'YouTube'
};

const getChannelDisplayName = (prefix: string): string =>
  CHANNEL_DISPLAY_NAME[prefix] ?? titleize(prefix);

// A formula references a metric's *value* via `metric.volume`/`metric.revenue` tokens —
// each one needs its own KPI picker so the formula knows which KPI's volume/revenue to pull.
const isKpiToken = (token: string): boolean =>
  token.includes('volume') || token.includes('revenue');

interface CustomMetricsModalProps {
  columnMetaDataMap: ColumnMetaDataMap;
  customMetric?: CustomMetric;
  disabled?: boolean;
  kpiMetaData: Record<string, KpiInfo>;
  onHide: () => void;
  prefix: SingleChannelMediaTypes | 'cross_channel' | 'pacing';
  save: (metric: Partial<CustomMetric>) => void;
  // Shows the Decimals input; when hidden, the saved metric uses 0 decimals.
  showDecimals?: boolean;
}

export const CustomMetricsModal = ({
  columnMetaDataMap,
  customMetric,
  disabled = false,
  kpiMetaData,
  onHide,
  prefix,
  save,
  showDecimals = false
}: CustomMetricsModalProps) => {
  const [compileError, setCompileError] = useState<string>();
  const [cursorPosition, setCursorPosition] = useState(0);
  const [description, setDescription] = useState<string>();
  const [format, setFormat] = useState<string>(customMetric ? customMetric.formatter : 'number');
  const [formula, setFormulaRaw] = useState<string>();
  const [formulaKPIs, setFormulaKPIs] = useState<CustomMetricKPI[]>();
  const [displayName, setDisplayName] = useState<string>();
  const [decimals, setDecimals] = useState<string>();
  const descriptionAreaRef = useRef<HTMLTextAreaElement>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const { cid } = useCompanyInfo();

  const availableMetricsMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const dataVarName of Object.keys(columnMetaDataMap)) {
      const meta = columnMetaDataMap[dataVarName] as ColumnMetaData;
      if (!meta.aggregator && !meta.isCustom) {
        map[dataVarName] = meta.prettyName.replace('(000s)', '');
      }
    }
    return map;
  }, [columnMetaDataMap]);

  // Other companies' custom metrics on this channel, used to catch a formula that reinvents one
  // of them under a new name (see findDuplicateMetric below).
  const existingCustomMetrics = useMemo(
    () =>
      Object.keys(columnMetaDataMap)
        .map(dataVarName => columnMetaDataMap[dataVarName]?.customMetaData)
        .filter((metric): metric is CustomMetric => Boolean(metric)),
    [columnMetaDataMap]
  );

  const updateCursorPosition = useCallback(() => {
    const pos = textAreaRef.current?.selectionStart;
    if (pos != null) {
      setCursorPosition(pos);
    }
  }, []);

  const currentFormulaTokens = useMemo(
    () => (formula ? tokenizeFormula(formula, availableMetricsMap) : undefined),
    [availableMetricsMap, formula]
  );

  const formulaToSave = useMemo(
    () =>
      (currentFormulaTokens ?? []).reduce(
        (acc, token) => `${acc}${columnMetaDataMap[token] ? 'fetch.' : ''}${token}`,
        ''
      ),
    [columnMetaDataMap, currentFormulaTokens]
  );

  const suggestedNextTokens = useMemo(() => {
    const tokensBeforeCursor: string[] = [];
    let textIndex = 0;
    for (const token of currentFormulaTokens ?? []) {
      if (textIndex + token.length <= cursorPosition) {
        tokensBeforeCursor.push(token);
      }
      textIndex += token.length;
    }
    return suggestNextTokens(tokensBeforeCursor, Object.keys(availableMetricsMap));
  }, [availableMetricsMap, currentFormulaTokens, cursorPosition]);

  const kpiOptions = useMemo(
    () => [
      { label: 'Global KPI', value: GLOBAL_KPI },
      ...convertKPIMapToOptions(prefix, kpiMetaData)
    ],
    [kpiMetaData, prefix]
  );

  const setFormula = useCallback(
    (text: string) => {
      setFormulaRaw(text);
      updateCursorPosition();
    },
    [updateCursorPosition]
  );

  const forceCursorPosition = useCallback((pos: number) => {
    if (!textAreaRef.current) return;
    textAreaRef.current.setSelectionRange(pos, pos);
    setCursorPosition(pos);
  }, []);

  const insertText = useCallback(
    (text: string) => {
      const tokens = currentFormulaTokens ?? [];
      const formulaLength = tokens.reduce((sum, token) => sum + token.length, 0);
      const selectionStart =
        textAreaRef.current?.selectionStart != null &&
        document.activeElement === textAreaRef.current
          ? textAreaRef.current.selectionStart
          : (cursorPosition ?? formulaLength);

      let textIndex = 0;
      let newFormula = '';
      let insertedText = false;
      for (const token of tokens) {
        if (textIndex + token.length > selectionStart && !insertedText) {
          newFormula = `${newFormula}${text}${token}`;
          textIndex += token.length;
          insertedText = true;
        } else {
          newFormula = `${newFormula}${token}`;
          textIndex += insertedText ? 0 : token.length;
        }
      }
      setFormula(insertedText ? newFormula : `${newFormula}${text}`);
      forceCursorPosition(insertedText ? textIndex : `${newFormula}${text}`.length);
    },
    [currentFormulaTokens, cursorPosition, forceCursorPosition, setFormula]
  );

  const saveMetric = useCallback(() => {
    const { message } = testFormula(formulaToSave, Object.keys(availableMetricsMap));
    // `name` is the metric's PERMANENT id -- it is the `dataVarName` key stored in
    // metrics_table_presets (see V1_1785810577, which repoints presets by matching on it) and, going
    // forward, the id saved inside dashboarding ChartConfigs. Re-deriving it from the display name on
    // every save silently orphaned both. Derive it at CREATE time only; an edit renames
    // `display_name` -- the sole name field this modal exposes -- and leaves `name` frozen, so the two
    // may legitimately drift, exactly as they do in the static registry (`spend` / "Spend").
    const derivedName = (displayName ?? '').toLowerCase().replaceAll(' ', '_');
    const name = customMetric?.id ? (customMetric.name ?? derivedName) : derivedName;
    if (message) {
      setCompileError(
        `There was an error with your metric: ${message}. Please update and retry saving.`
      );
      return;
    }
    // Validate what the user actually typed, not the frozen id -- on an edit `name` is populated from
    // the existing record and would mask a blank Metric Name field.
    if (!derivedName) {
      setCompileError('A name is required for a custom metric');
      return;
    }
    const usedNames = Object.keys(columnMetaDataMap).filter(
      dataVarName => !customMetric || dataVarName !== customMetric.name
    );
    if (usedNames.includes(name)) {
      setCompileError(`${name} is already used as a metric, please rename`);
      return;
    }
    if (formulaToSave.length === 0) {
      setCompileError('A formula is required for a custom metric');
      return;
    }
    const duplicateMetric = findDuplicateMetric(
      formulaToSave,
      prefix,
      [cid],
      customMetric
        ? existingCustomMetrics.filter(metric => metric.id !== customMetric.id)
        : existingCustomMetrics,
      formulaKPIs ?? []
    );
    if (duplicateMetric) {
      setCompileError(
        duplicateMetric.type === DuplicateMetricType.STANDARD
          ? `This formula is the same as the standard "${duplicateMetric.prettyName}" metric -- use that instead of creating a custom metric.`
          : `This formula is the same as the existing custom metric "${duplicateMetric.prettyName}".`
      );
      return;
    }
    const isFunction = [...OPERATORS, ...FUNCTIONS].some(symbol => formulaToSave.includes(symbol));
    if (!isFunction) {
      setCompileError(`${formulaToSave.replaceAll('fetch.', '')} already exists.`);
      return;
    }
    setCompileError(undefined);
    const requiredColumns = (currentFormulaTokens ?? []).filter(
      token => availableMetricsMap[token]
    );
    save({
      ...customMetric,
      name,
      companies: [cid],
      channel: prefix,
      decimals: showDecimals ? Number(decimals ?? 0) : 0,
      description,
      display_name: displayName,
      function_string: formulaToSave,
      formatter: format,
      min_is_best: false,
      required_columns: [...new Set(requiredColumns)],
      kpis: formulaKPIs ?? []
    });
  }, [
    decimals,
    showDecimals,
    availableMetricsMap,
    cid,
    columnMetaDataMap,
    currentFormulaTokens,
    customMetric,
    description,
    displayName,
    existingCustomMetrics,
    format,
    formulaKPIs,
    formulaToSave,
    prefix,
    save
  ]);

  const onDescriptionInput = useCallback(() => {
    const el = descriptionAreaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height =
      el.scrollHeight > el.clientHeight ? `${el.scrollHeight}px` : `${el.clientHeight}px`;
  }, []);

  useEffect(() => {
    if (!customMetric) return;
    if (formula === undefined) {
      setFormula(customMetric.function_string.replace(/fetch\./g, ''));
    }
    if (description === undefined) {
      setDescription(customMetric.description);
    }
    if (displayName === undefined) {
      setDisplayName(customMetric.display_name);
    }
    if (formulaKPIs === undefined) {
      setFormulaKPIs(customMetric.kpis);
    }
    if (decimals === undefined) {
      setDecimals(`${customMetric.decimals}`);
    }
  }, [customMetric, decimals, description, displayName, formula, formulaKPIs, setFormula]);

  // Every `volume`/`revenue` token needs a matching KPI-picker entry — grow/shrink
  // `formulaKPIs` to track the current token list, defaulting new entries to GLOBAL_KPI.
  useEffect(() => {
    const kpiTokens = (currentFormulaTokens ?? []).filter(isKpiToken);
    if (!customMetric && formulaKPIs === undefined) {
      setFormulaKPIs([]);
    } else if (formulaKPIs && formulaKPIs.length !== kpiTokens.length) {
      setFormulaKPIs(
        kpiTokens.map(
          (token, i) => formulaKPIs[i] ?? { dataVarName: token, kpi: GLOBAL_KPI, SoT: '' }
        )
      );
    }
  }, [currentFormulaTokens, customMetric, formulaKPIs]);

  const suggestionTokens = useMemo(
    () => [...new Set([OPEN_PAREN, CLOSE_PAREN, ...OPERATORS, ...FUNCTIONS])],
    []
  );

  return (
    <Modal
      centered
      className={cn('custom-metrics-modal', { disabled })}
      data-testid='custom-metrics-modal'
      onHide={onHide}
      show
      size='lg'
    >
      <Modal.Header>
        <div className='title-container'>
          <Modal.Title>{customMetric ? 'Edit' : 'New'} Custom Metric:</Modal.Title>
          <div className='channel-label'>{getChannelDisplayName(prefix)}</div>
        </div>
        <IconButton appearance={Appearance.EMPTY} design={Design.NEUTRAL} onClick={onHide}>
          <MdClose />
        </IconButton>
      </Modal.Header>
      <Modal.Body>
        <div className='metric-settings-container'>
          <div className='name'>
            <div className='label'>Metric Name*</div>
            <Form.Control
              data-testid='custom-metric-name-input'
              onChange={e => setDisplayName(e.target.value.slice(0, 20))}
              placeholder='Ex: ACOS'
              size='lg'
              value={displayName ?? ''}
            />
          </div>
          <div className='description'>
            <div className='label'>Description</div>
            <Form.Control
              as='textarea'
              onChange={e => setDescription(e.target.value.slice(0, 200))}
              onInput={onDescriptionInput}
              placeholder='Ex: Amazon Cost of Sales'
              ref={descriptionAreaRef}
              rows={1}
              style={{ resize: 'none', overflow: 'hidden' }}
              value={description ?? ''}
            />
          </div>
          <div className='format'>
            <div className='label'>Format</div>
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              options={FORMAT_OPTIONS}
              onChange={setFormat}
              size={Size.LG}
              value={format}
            />
          </div>
          {showDecimals && (
            <div className='decimals'>
              <div className='label'>Decimals</div>
              <Form.Control
                max={MAX_DECIMALS}
                min={0}
                onChange={e => {
                  // Only allow empty or a single digit from 0 to MAX_DECIMALS
                  const { value } = e.target;
                  if (value === '' || (/^\d$/.test(value) && Number(value) <= MAX_DECIMALS)) {
                    setDecimals(value);
                  }
                }}
                size='lg'
                step={1}
                type='number'
                value={decimals ?? ''}
              />
            </div>
          )}
        </div>
        <div className='formula-container'>
          <Form.Control
            as='textarea'
            className='formula-text-box'
            data-testid='custom-metric-formula-input'
            onChange={e => {
              setFormula(e.currentTarget.value);
              setCompileError(undefined);
            }}
            onClick={updateCursorPosition}
            onFocus={updateCursorPosition}
            onKeyUp={updateCursorPosition}
            placeholder='Add metrics and functions using the tokens below. Add numerical values using your keyboard.'
            ref={textAreaRef}
            rows={6}
            spellCheck={false}
            value={formula ?? ''}
          />
          <SuggestionButtons
            availableMetricsMap={availableMetricsMap}
            onSelect={insertText}
            suggestedNextTokens={suggestedNextTokens}
            tokens={suggestionTokens}
          />
          <KpiMappingList
            formulaKPIs={formulaKPIs}
            kpiOptions={kpiOptions}
            onChange={setFormulaKPIs}
          />
          <div className='calcs-message'>
            Calculated metrics (i.e. ROAS, CPX) must be entered as their equations.
          </div>
          {compileError && <div className='compile-error'>{compileError}</div>}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button design={Design.NEUTRAL} appearance={Appearance.OUTLINED} onClick={onHide}>
          Cancel
        </Button>
        <Button
          data-testid='custom-metric-save-button'
          disabled={disabled}
          leadingElement={<MdSave />}
          onClick={saveMetric}
        >
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

interface SuggestionButtonsProps {
  availableMetricsMap: Record<string, string>;
  onSelect: (token: string) => void;
  suggestedNextTokens: ReturnType<typeof suggestNextTokens>;
  tokens: string[];
}

const SuggestionButtons = ({
  availableMetricsMap,
  onSelect,
  suggestedNextTokens,
  tokens
}: SuggestionButtonsProps) => (
  <div className='suggestion-lists'>
    <div className='suggestion-list'>
      {tokens.map(token => {
        const suggestions = [
          ...(suggestedNextTokens.operators ?? []),
          ...(suggestedNextTokens.functions ?? [])
        ];
        return (
          <Button
            className='suggestion'
            data-testid={`custom-metric-suggestion-${token}`}
            design={Design.NEUTRAL}
            disabled={!suggestions.includes(token) && !suggestions.includes(`${token}(`)}
            key={token}
            onClick={() => onSelect(token)}
          >
            {OPERATOR_LABEL_MAP[token] ?? token}
          </Button>
        );
      })}
    </div>
    <div className='divider' />
    <div className='suggestion-list'>
      {Object.keys(availableMetricsMap).map(token => (
        <Button
          className='suggestion'
          data-testid={`custom-metric-suggestion-${token}`}
          design={Design.NEUTRAL}
          disabled={!suggestedNextTokens.availableVariables?.includes(token)}
          key={token}
          onClick={() => onSelect(token)}
        >
          {availableMetricsMap[token]}
        </Button>
      ))}
    </div>
  </div>
);

interface KpiMappingListProps {
  formulaKPIs?: CustomMetricKPI[];
  kpiOptions: { label: string; value: string }[];
  onChange: (formulaKPIs: CustomMetricKPI[]) => void;
}

const KpiMappingList = ({ formulaKPIs = [], kpiOptions, onChange }: KpiMappingListProps) => {
  if (formulaKPIs.length === 0) return null;

  return (
    <div className='kpi-mapping-list'>
      {formulaKPIs.map((kpiInfo, i) => (
        <div className='kpi-selector' key={i}>
          <div className='label'>KPI {i + 1}</div>
          <Dropdown
            overlayPlacement={Placement.RIGHT}
            size={Size.LG}
            onChange={kpi =>
              onChange(formulaKPIs.map((info, j) => (j === i ? { ...info, kpi } : info)))
            }
            options={kpiOptions}
            placeholder='Select a KPI'
            value={kpiInfo.kpi}
          />
        </div>
      ))}
    </div>
  );
};
